import * as actionTypes from './actionTypes'
import { handleActions } from 'redux-actions'

import { merge } from 'lodash/fp'

const reducers = {}

reducers[actionTypes.CONNECT] = {
  next (state) {
    return {
      ...state,
      isPending: true,
      error: undefined
    }
  }
}

reducers[actionTypes.SET_CONNECTION_INFO] = {
  next (state, action) {
    return {
      ...state,
      platform: action.meta.platform,
      connection: {
        ...state.connection,
        [action.meta.platform]: action.payload.connection
      },
      userInfo: action.payload.userInfo
    }
  }
}

reducers[actionTypes.CONNECT_FINISHED] = {
  next (state, action) {
    return {
      ...state,
      isConnected: true,
      isPending: false,
      error: undefined,
      isSSO: action.meta.isSSO,
      platform: action.meta.platform,
      // Separate subscription, connection info based on the platform used.
      // Store platform subscription information.
      subscription: {
        ...state.subscription,
        [action.meta.platform]: action.payload.subscription
      },
      // Store platform connection information to be provided to other plugins.
      connection: action.payload.connection
        ? { ...state.connection, [action.meta.platform]: action.payload.connection }
        : { ...state.connection },
      // Store user information to be provided to developers.
      userInfo: action.payload.userInfo ? action.payload.userInfo : state.userInfo
    }
  },
  throw (state, action) {
    return {
      ...state,
      isConnected: false,
      isPending: false,
      error: action.payload
    }
  }
}

// On connection occurred, store the connection information into state, but do
// not update any status state. The connection has not yet finished.
reducers[actionTypes.CONNECTION_OCCURRED] = {
  next (state, action) {
    return {
      ...state,
      // Separate subscription, connection info based on the platform used.
      // Store platform subscription information.
      subscription: {
        ...state.subscription,
        [action.meta.platform]: action.payload.subscription
      },
      // Store platform connection information to be provided to other plugins.
      connection: {
        ...state.connection,
        [action.meta.platform]: action.payload.connection
      }
    }
  }
}

reducers[actionTypes.DISCONNECT] = {
  next (state) {
    return {
      ...state,
      isPending: true,
      error: undefined
    }
  }
}

/*
 * Clear the authentication state when we disconnect.
 * Note: This should only be for 3.X (and 4.X backwards-compatible connect API)
 *    authentication.
 */
reducers[actionTypes.DISCONNECT_FINISHED] = {
  next (state, action) {
    return {
      isConnected: false,
      isPending: false,
      error: undefined
    }
  },
  throw (state, action) {
    return {
      ...state,
      // Treat an error as if we are still disconnected.
      isConnected: false,
      isPending: false,
      error: action.payload
    }
  }
}

reducers[actionTypes.REFRESH_TOKENS_FINISHED] = {
  next (state, action) {
    // Merge the updated requestOptions into the current state.
    const requestOptions = merge(
      state.connection[action.meta.platform].requestOptions,
      action.payload.connection.requestOptions
    )

    return {
      ...state,
      connection: {
        ...state.connection,
        [action.meta.platform]: {
          ...state.connection[action.meta.platform],
          ...action.payload.connection,
          requestOptions
        }
      },
      userInfo: {
        ...state.userInfo,
        accessToken: action.payload.connection.accessToken,
        refreshToken: action.payload.connection.refreshToken
      }
    }
  }
}

/*
 * Updates the subscription information for a specified platform.
 */
reducers[actionTypes.UPDATE_SUBSCRIPTION_FINISH] = {
  next (state, action) {
    return {
      ...state,
      subscription: {
        ...state.subscription,
        [action.meta.platform]: {
          ...state.subscription[action.meta.platform],
          ...action.payload
        }
      }
    }
  }
}

reducers[actionTypes.SET_TOKEN] = {
  next (state, action) {
    // We received an idToken which is a JWT token that represents the identity of
    // the user. This token contains information about the user. For now we only need
    // the username. In the future we might use more from this token.
    const idTokenPayload = action.payload.idToken
    return {
      ...state,
      userInfo: {
        ...state.userInfo,
        accessToken: action.payload.accessToken,
        username: idTokenPayload.preferred_username,
        identity: idTokenPayload['services-identity']
      }
    }
  }
}

reducers[actionTypes.SET_CREDENTIALS_FINISH] = {
  next (state, action) {
    return {
      ...state,
      error: undefined,
      platform: action.meta.platform,
      // Store platform connection information to be provided to other plugins.
      connection: {
        ...state.connection,
        [action.meta.platform]: action.payload.connection
      },
      // Store user information to be provided to developers.
      userInfo: action.payload.userInfo
    }
  },
  throw (state, action) {
    return {
      ...state,
      error: action.payload
    }
  }
}

/*
 * Remove the x-token from the request header after subscription is successful.
 */
reducers[actionTypes.UPDATE_HMAC_TOKEN] = {
  next (state, action) {
    const { 'x-token': _removed, ...removeXtoken } = state.connection[action.meta.platform].requestOptions.headers

    const headers = {
      ...removeXtoken,
      'x-session': action.payload
    }

    return {
      ...state,
      connection: {
        ...state.connection,
        [action.meta.platform]: {
          ...state.connection[action.meta.platform],
          requestOptions: {
            ...state.connection[action.meta.platform].requestOptions,
            headers
          }
        }
      }
    }
  }
}

/**
 * Auth Interface reducer
 * @method reducer
 * @param  {Object}  state - The current redux state.
 * @param  {Object}  action - A flux standard action.
 * @return {Object}  - The new redux state.
 * @example
 * Auth state structure example; connected.
 * authState = {
 *     isConnected: true,
 *     isPending: false,
 *     error: undefined,
 *     subscription: { ... },
 *     connection: { ... },
 *     userInfo: { ... }
 * }
 */
const reducer = handleActions(reducers, {
  isConnected: false,
  isPending: false
})
export default reducer
